import * as R from 'ramda';
import { redirect } from 'redux-first-router';
import def from './define-action';

const INITIAL_STATE = {
    modalOptions: {},
    modalTextValues: {
        firstName: '',
        lastName: '',
        email: '',
        oldPassword: '',
        newPassword: '',
        newPasswordConfirmed: '',
        code: '',
        description: '',
    },
};

const resetModalTextValues = {
    oldPassword: '',
    newPassword: '',
    newPasswordConfirmed: '',
    code: '',
    description: '',
};

// =====================================================================================================================
// Action Constants
// =====================================================================================================================

const ActionConstants = {
    CLEAR_MODAL_TEXT_VALUES: 'CLEAR_MODAL_TEXT_VALUES',
    HIDE_MODAL: 'HIDE_MODAL',
    SET_MODAL_TEXT_VALUES: 'SET_MODAL_TEXT_VALUES',
    SHOW_MODAL: 'SHOW_MODAL',
};

// =====================================================================================================================
// Actions
// =====================================================================================================================

const Actions = {
    clearModalTextValues: def(ActionConstants.CLEAR_MODAL_TEXT_VALUES),
    hideModal: def(ActionConstants.HIDE_MODAL),
    setModalTextValues: def(ActionConstants.SET_MODAL_TEXT_VALUES, 'values'),
    showModal: def(ActionConstants.SHOW_MODAL, 'options'),
};

// =====================================================================================================================
// Thunks
// =====================================================================================================================

/*
 * Set the input options for the profile modal.
 * @sig showProfileModal :: Func -> DispatchFunction -> ()
 */
const showProfileModal = (handleSubmit, handleClose) => dispatch => {
    const options = {
        textOptions: [
            { label: 'email', value: 'email' },
            { label: 'First Name', value: 'firstName' },
            { label: 'Last Name', value: 'lastName' },
        ],
        buttonText: 'Update Profile',
        handleSubmit,
        handleClose,
        title: 'Edit Profile',
        name: 'form',
    };

    dispatch(Actions.showModal(options));
};

/*
 * Set the input options for the profile modal.
 * Passwords should be hidden.
 * @sig showPasswordModal :: Func -> DispatchFunction -> ()
 */
const showPasswordModal = (handleSubmit, handleClose) => dispatch => {
    const options = {
        textOptions: [
            { label: 'Old Password', value: 'oldPassword', password: true },
            { label: 'New Password', value: 'newPassword', password: true },
            { label: 'New Password Confirmed', value: 'newPasswordConfirmed', password: true },
        ],
        buttonText: 'Change Password',
        handleSubmit,
        handleClose,
        title: 'Change Password',
        name: 'form',
    };

    dispatch(Actions.showModal(options));
};

const showSupportModal = (handleSubmit, handleClose) => dispatch => {
    const options = {
        textOptions: [
            {
                text: 'Let us know if you have a question or are experiencing an issue with the VMS. You can also read the feature guide and FAQs at ',
                link: 'https://docs.atrius.com/',
            },
        ],
        buttonText: 'Email Support',
        handleClose,
        handleSubmit,
        icon: 'icon-supportheadphones',
        iconColor: 'gray',
        name: 'alert',
        title: 'Contact Support',
    };

    dispatch(Actions.showModal(options));
};

const showVerifyEmailModal = handleSubmit => dispatch => {
    const options = {
        textOptions: [{ label: 'Code', value: 'code' }],
        buttonText: 'Verify',
        handleSubmit,
        title: 'Verify Email',
        name: 'form',
    };

    dispatch(Actions.showModal(options));
};

const showSuccessModal = (handleClose, message) => dispatch => {
    const options = {
        buttonText: 'Continue',
        handleClose,
        handleSubmit: handleClose,
        icon: 'icon-circle-check',
        iconColor: 'green',
        name: 'alert',
        textOptions: [{ text: message }],
        title: 'Success!',
    };

    dispatch(Actions.showModal(options));
};

const setUserDataValues = userData => dispatch => {
    if (!userData) return;

    const userDataValues = {
        firstName: userData.given_name,
        lastName: userData.family_name,
        email: userData.email,
        name: `${userData.given_name} ${userData.family_name}`,
    };

    dispatch(Actions.setModalTextValues(userDataValues));
};

const showVenueChangeAlert = location => async dispatch => {
    const handleClose = () => {
        dispatch(Actions.hideModal());
        dispatch(Actions.clearModalTextValues());
    };

    const options = {
        buttonText: 'Continue',
        textOptions: [{ text: 'Please wait for saving to complete.' }],
        handleClose,
        handleSubmit: handleClose,
        icon: 'icon-loader',
        iconColor: 'gray',
        name: 'alert',
        title: 'Unsaved changes for current venue',
    };

    await dispatch(Actions.showModal(options));
    dispatch(redirect(location));
};

const Thunks = {
    showProfileModal,
    showPasswordModal,
    showSupportModal,
    showVerifyEmailModal,
    showSuccessModal,
    setUserDataValues,
    showVenueChangeAlert,
};

// =====================================================================================================================
// Reducer
// =====================================================================================================================

const reducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case ActionConstants.CLEAR_MODAL_TEXT_VALUES:
            return handleClearModalTextValues(state, action);
        case ActionConstants.HIDE_MODAL:
            return { ...state, modalOptions: {} };
        case ActionConstants.SET_MODAL_TEXT_VALUES:
            return handleSetModalTextValues(state, action);
        case ActionConstants.SHOW_MODAL:
            return { ...state, modalOptions: action.options };
        default:
            return state;
    }
};

const handleSetModalTextValues = (state, action) => {
    const { modalTextValues } = state;

    return { ...state, modalTextValues: R.mergeRight(modalTextValues, action.values) };
};

const handleClearModalTextValues = state => {
    const { modalTextValues } = state;

    return { ...state, modalTextValues: R.mergeRight(modalTextValues, resetModalTextValues) };
};

// =====================================================================================================================
// Selectors
// =====================================================================================================================

const getModalOptions = state => R.path(['modal', 'modalOptions'], state);
const getModalTextValues = state => R.path(['modal', 'modalTextValues'], state);

/*
 * Return only changes to the user's profile data (and empty strings for unchanged)
 */
const getProfileValueChanges = (masterState, userData) => {
    const values = getModalTextValues(masterState);

    const email = values.email !== userData.email ? values.email : '';
    const firstName = values.firstName !== userData.given_name ? values.firstName : '';
    const lastName = values.lastName !== userData.family_name ? values.lastName : '';

    return {
        email,
        firstName,
        lastName,
    };
};

const Selectors = {
    getModalOptions,
    getModalTextValues,

    getProfileChanges: getProfileValueChanges,
};

export default reducer;

export { reducer, Selectors, Actions, Thunks };
